type RGB = [number, number, number];

/**
 * Converts a hex color to an RGB array.
 * Supports both 3-character and 6-character hex codes.
 * @param hex - The hex color string (e.g., "#fff" or "#ffffff").
 * @returns An array of RGB values [r, g, b].
 */
function hexToRgb(hex: string): [number, number, number] {
    // Remove the leading '#' if present
    hex = hex.replace(/^#/, '');

    // Expand 3-character hex to 6-character hex
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((char) => char + char)
            .join('');
    }

    // Parse the 6-character hex
    const bigint = parseInt(hex, 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

/**
 * Calculates the relative luminance of an RGB color.
 * @param rgb - An array of RGB values [r, g, b].
 * @returns The relative luminance (a number between 0 and 1).
 */
function luminance(rgb: RGB): number {
    const [r, g, b] = rgb.map((v) => {
        const normalized = v / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the contrast ratio between two colors.
 * @param color1 - The first color in hex format (e.g., "#ffffff").
 * @param color2 - The second color in hex format (e.g., "#000000").
 * @returns The contrast ratio (a number >= 1).
 */
export function getContrastRatio(color1: string, color2: string): number {
    const lum1 = luminance(hexToRgb(color1));
    const lum2 = luminance(hexToRgb(color2));
    const [lighter, darker] = lum1 > lum2 ? [lum1, lum2] : [lum2, lum1];
    return (lighter + 0.05) / (darker + 0.05);
}

export function getTextColorFromBackground(bgColor: string): string {
    const whiteContrast = getContrastRatio('#ffffff', bgColor);
    const blackContrast = getContrastRatio('#000000', bgColor);
    return whiteContrast >= blackContrast ? '#ffffff' : '#000000';
}
